import {isObject} from 'lodash';

import {collectRecordEntries, isDefined} from '../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {
  AnalyticsFilterRelationsEnum,
  AnalyticsFilterTypesEnum,
  AnalyticsSavedViewFiltersModel,
  AnalyticsSavedViewTeammatesTerm,
  AnalyticsSavedViewTerm,
  AnalyticsTagModesEnum,
  isInboxScopeTerm,
  isTagTerm,
  isTermWithIds,
  isTermWithoutTags,
  RAnalyticsInboxScopeFiltersEnum,
  RAnalyticsLocales,
  RAnalyticsSavedViewTeammatesFilter,
} from '../../models/api/analyticsSavedViewModel';
import {
  ANALYTICS_FILTERS_TAG_MODE,
  ANALYTICS_FILTERS_TAG_RELATION,
  AnalyticsPanelFiltersState,
  DEFAULT_FILTERS_STATE,
  EMPTY_FILTERS_STATE,
  FiltersStateKey,
} from '../../store/panels/analytics/filters/analyticsPanelFiltersState';
import {ReportFilterOptionsEnum} from './reportHelpers/reportTypes';

/**
 * Map filters to view terms.
 */
export const mapFiltersToTerms = (
  filters: AnalyticsPanelFiltersState,
  keysToKeep?: ReadonlyArray<FiltersStateKey>,
): Array<AnalyticsSavedViewTerm> =>
  collectRecordEntries(filters)
    .map(([filterKey, filterValues]): AnalyticsSavedViewTerm | undefined => {
      if (keysToKeep && !keysToKeep.includes(filterKey)) {
        return undefined;
      }
      // Exclude tags is a negated tag term.
      const type =
        filterKey === AnalyticsFilterTypesEnum.EXCLUDE_TAGS ? AnalyticsFilterTypesEnum.TAGS : filterKey;
      const negated = filterKey === AnalyticsFilterTypesEnum.EXCLUDE_TAGS;

      // Without tags is a special term.
      if (type === AnalyticsFilterTypesEnum.WITHOUT_TAGS && filterValues) {
        return {
          type,
          negated,
        };
      }

      // Tag relation and mode aren't real filters.
      if (type === ANALYTICS_FILTERS_TAG_RELATION || type === ANALYTICS_FILTERS_TAG_MODE) {
        return undefined;
      }

      if (type === AnalyticsFilterTypesEnum.LOCALE && RAnalyticsLocales.is(filterValues)) {
        return {
          type,
          locales: filterValues,
        };
      }

      if (type === AnalyticsFilterTypesEnum.INBOXES && RAnalyticsInboxScopeFiltersEnum.is(filterValues)) {
        return {
          type,
          special_value: filterValues,
        };
      }

      if (
        type === AnalyticsFilterTypesEnum.TEAMMATES &&
        RAnalyticsSavedViewTeammatesFilter.is(filterValues)
      ) {
        if (filterValues.teammate_ids.length === 0 && filterValues.circle_ids.length === 0) {
          return undefined;
        }

        return {
          type,
          relation: AnalyticsFilterRelationsEnum.OR,
          ids: filterValues.teammate_ids,
          circle_ids: filterValues.circle_ids,
          negated,
        };
      }

      if (filterValues && Array.isArray(filterValues) && filterValues.length) {
        // Normal terms.
        const relation =
          filterKey === AnalyticsFilterTypesEnum.TAGS
            ? filters[ANALYTICS_FILTERS_TAG_RELATION]
            : AnalyticsFilterRelationsEnum.OR;

        return {
          type,
          relation,
          ids: filterValues,
          negated,
        };
      }

      return undefined;
    })
    .filter(isDefined);

/**
 * Map view terms to filters.
 */
export const mapTermsToFilters = ({
  terms,
  options,
}: AnalyticsSavedViewFiltersModel): AnalyticsPanelFiltersState => {
  const filters = terms.filter(isTermWithIds).reduce(
    (acc, term) => ({
      ...acc,
      [getFilterTypeFromTerm(term)]: term.ids,
    }),
    {},
  );

  const specialValueFilters = terms.filter(isInboxScopeTerm).reduce(
    (acc, term) => ({
      ...acc,
      [getFilterTypeFromTerm(term)]: term.special_value,
    }),
    {},
  );

  const teammateTerm = terms.find(
    (term): term is AnalyticsSavedViewTeammatesTerm => term.type === AnalyticsFilterTypesEnum.TEAMMATES,
  );

  return {
    ...EMPTY_FILTERS_STATE,
    ...filters,
    ...specialValueFilters,
    [AnalyticsFilterTypesEnum.TEAMMATES]: teammateTerm
      ? {
          teammate_ids: teammateTerm.ids,
          circle_ids: teammateTerm.circle_ids ?? [],
        }
      : undefined,
    [ANALYTICS_FILTERS_TAG_RELATION]: terms.find(isTagTerm)?.relation || AnalyticsFilterRelationsEnum.OR,
    [ANALYTICS_FILTERS_TAG_MODE]:
      options?.[ReportFilterOptionsEnum.TAG_MODE] || AnalyticsTagModesEnum.TAG_STILL_ON_CONV,
    [AnalyticsFilterTypesEnum.WITHOUT_TAGS]: Boolean(terms.find(isTermWithoutTags)),
  };
};

export const getFilterTypeFromTerm = (term: AnalyticsSavedViewTerm) =>
  term.type === AnalyticsFilterTypesEnum.TAGS && term.negated
    ? AnalyticsFilterTypesEnum.EXCLUDE_TAGS
    : term.type;

const analyticsResourceFilterTypes = [
  AnalyticsFilterTypesEnum.ACCOUNTS,
  AnalyticsFilterTypesEnum.CHANNELS,
  AnalyticsFilterTypesEnum.INBOXES,
  AnalyticsFilterTypesEnum.TAGS,
  AnalyticsFilterTypesEnum.EXCLUDE_TAGS,
  AnalyticsFilterTypesEnum.FULL_TAGS,
  AnalyticsFilterTypesEnum.TEAMMATES,
  AnalyticsFilterTypesEnum.TEAMS,
] as const;
const analyticsResourceFilterTypesSet = new Set<AnalyticsFilterTypesEnum>(analyticsResourceFilterTypes);

type AnalyticsResoureFilterType = (typeof analyticsResourceFilterTypes)[0];

export function isResourceKey(type: AnalyticsFilterTypesEnum): type is AnalyticsResoureFilterType {
  return analyticsResourceFilterTypesSet.has(type);
}

/**
 * This function check if some filters have been selected
 * and it's not corresponding to default inbox filters.
 * It is used to check if a view need to be saved or reset due to new filters selection
 */
export const isFilterValueNotEmpty = (
  value?:
    | ReadonlyArray<number | string>
    | boolean
    | string
    | {teammate_ids: ReadonlyArray<number>; circle_ids: ReadonlyArray<number>},
) => {
  // Special values
  if (RAnalyticsInboxScopeFiltersEnum.is(value) && value !== DEFAULT_FILTERS_STATE.inboxes) {
    return true;
  }

  if (
    isObject(value) &&
    (('teammate_ids' in value && value.teammate_ids.length) ||
      ('circle_ids' in value && value.circle_ids.length))
  ) {
    return true;
  }

  // Tag mode and relation are ignored.
  if (typeof value === 'string') {
    return false;
  }

  // Without tags: has a value if true.
  if (typeof value === 'boolean') {
    return value;
  }

  // Default case: list of ids.
  return Array.isArray(value) && Boolean(value.length);
};
